import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import CancelSubmissionModal from "@/components/CancelSubmissionModal";
import { resolveInviteStatus } from "@/lib/statusResolvers";  // Phase STATE-DRIFT-SWEEP

export default function InviteLandingPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [searchParams] = useSearchParams();
    const [invite, setInvite] = useState(null);
    const [error, setError] = useState("");
    const [cancelledInvite, setCancelledInvite] = useState(null);  // Phase CANCEL-DRAFT
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(false);
    // Phase CANCEL-DRAFT — modal open state for the "Cancel this invite" footer link.
    const [cancelOpen, setCancelOpen] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const { data } = await api.get(`/invites/lookup`, { params: { token } });
                if (active) setInvite(data);
            } catch (err) {
                // Phase CANCEL-DRAFT — structured 410 with code=invite_cancelled
                // renders the friendly cancelled-landing state instead of the
                // generic "This invite isn't available" copy.
                const detail = err?.response?.data?.detail;
                if (
                    err?.response?.status === 410
                    && typeof detail === "object"
                    && detail?.code === "invite_cancelled"
                ) {
                    if (active) setCancelledInvite(detail);
                } else if (active) {
                    setError(formatApiError(err));
                }
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [token]);

    // Auto-claim once user is authenticated as customer
    useEffect(() => {
        if (authLoading || !invite || !user) return;
        if (user.role !== "customer") {
            setError("This invite link can only be opened by a customer account.");
            return;
        }
        if (claiming) return;
        (async () => {
            setClaiming(true);
            try {
                const { data } = await api.post("/invites/claim", { token });
                toast.success("Invite linked — let's complete your application");
                navigate(`/customer/submissions/${data.submission_id}`, { replace: true });
            } catch (err) {
                setError(formatApiError(err));
            } finally {
                setClaiming(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, invite, user]);

    const goSignup = () => {
        const params = new URLSearchParams();
        if (invite?.customer_email) params.set("email", invite.customer_email);
        if (invite?.customer_name) params.set("name", invite.customer_name);
        params.set("redirect_to", `/invite/${token}`);
        navigate(`/signup?${params.toString()}`);
    };

    const goLogin = () => {
        const params = new URLSearchParams();
        if (invite?.customer_email) params.set("email", invite.customer_email);
        params.set("redirect_to", `/invite/${token}`);
        navigate(`/login?${params.toString()}`);
    };

    if (loading) {
        return (
            <AuthLayout eyebrow="Invite" title="Loading your invite…">
                <p data-testid="invite-loading" className="text-sm text-slate-500">One moment.</p>
            </AuthLayout>
        );
    }

    // Phase CANCEL-DRAFT — friendly cancelled-landing state.
    if (cancelledInvite) {
        return (
            <AuthLayout
                eyebrow="Invite"
                title="This application has been cancelled."
                subtitle="You'll need to request a new invite to continue."
            >
                <div
                    data-testid="invite-cancelled-landing"
                    className="rounded-md border border-red-200 bg-red-50/60 px-4 py-3 flex items-start gap-3"
                >
                    <XCircle size={18} className="text-red-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-red-900">
                        <p className="font-semibold">This application was cancelled.</p>
                        <p className="mt-1 text-red-800">
                            {cancelledInvite.cancelled_by_type === "customer"
                                ? "You cancelled this invite from a previous session."
                                : "Our team cancelled this invite."}
                            {cancelledInvite.cancelled_at
                                ? ` (${new Date(cancelledInvite.cancelled_at).toLocaleDateString()})`
                                : ""}
                        </p>
                    </div>
                </div>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button
                        onClick={() => navigate("/customer")}
                        data-testid="invite-cancelled-cta-login"
                        className="bg-blue-900 hover:bg-blue-800 h-11"
                    >
                        Sign in to your account
                    </Button>
                    <Button
                        onClick={() => (window.location.href = "mailto:support@example.com")}
                        variant="outline"
                        data-testid="invite-cancelled-cta-contact"
                        className="h-11"
                    >
                        Contact us
                    </Button>
                </div>
            </AuthLayout>
        );
    }

    if (error) {
        return (
            <AuthLayout
                eyebrow="Invite"
                title="This invite isn't available."
                subtitle={error}
            >
                <div data-testid="invite-error" className="rounded-md bg-red-50 border border-red-100 p-4 flex items-start gap-3">
                    <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                </div>
                <div className="mt-6">
                    <Button variant="outline" onClick={() => navigate("/login")} data-testid="invite-back-to-login">
                        Back to sign in
                    </Button>
                </div>
            </AuthLayout>
        );
    }

    if (claiming || (user && user.role === "customer" && !error)) {
        return (
            <AuthLayout eyebrow="Invite" title="Linking your invite…">
                <p data-testid="invite-claiming" className="text-sm text-slate-500">Just a moment while we set up your application.</p>
            </AuthLayout>
        );
    }

    const isLoggedInWrongRole = user && user.role !== "customer";

    return (
        <AuthLayout
            eyebrow={`Invite for ${invite.customer_name}`}
            title="Continue your rental application"
            subtitle={
                <>
                    We have an application waiting for <strong>{invite.customer_email}</strong>. Sign in or create your customer account to complete it.
                </>
            }
        >
            <div className="rounded-md border border-emerald-100 bg-emerald-50 p-4 flex items-start gap-3">
                <CheckCircle2 size={18} className="text-emerald-700 shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                    <p className="font-medium">Invite valid through {new Date(invite.expires_at).toLocaleDateString()}.</p>
                    <p className="text-emerald-800/80 mt-0.5" data-testid="invite-status-label">
                        {resolveInviteStatus(invite).label}
                    </p>
                </div>
            </div>

            {isLoggedInWrongRole ? (
                <div className="mt-6 rounded-md bg-amber-50 border border-amber-100 p-3 text-sm text-amber-800" data-testid="invite-wrong-role">
                    You're signed in as <strong>{user.email}</strong> ({user.role}). Sign out and use a customer account to claim this invite.
                </div>
            ) : (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button onClick={goSignup} data-testid="invite-cta-signup" className="bg-blue-900 hover:bg-blue-800 h-11">
                        Create my account
                    </Button>
                    <Button onClick={goLogin} variant="outline" data-testid="invite-cta-login" className="h-11">
                        I already have one
                    </Button>
                </div>
            )}

            {/* Phase CANCEL-DRAFT — footer "Cancel this invite" link. */}
            <div className="mt-8 pt-4 border-t border-slate-200 flex items-center justify-center">
                <button
                    type="button"
                    onClick={() => setCancelOpen(true)}
                    data-testid="invite-cancel-link"
                    className="text-xs text-slate-400 hover:text-red-700 underline-offset-2 hover:underline inline-flex items-center gap-1"
                >
                    <XCircle size={11} /> Cancel this invite
                </button>
            </div>

            <CancelSubmissionModal
                open={cancelOpen}
                onOpenChange={setCancelOpen}
                target={{ entityType: "invite", id: invite?.id }}
                surface="customer"
                invitePublicToken={token}
                fieldsFilledCount={0}
                filesCount={0}
                onCancelled={async () => {
                    // Immediately switch to the cancelled-landing branch
                    // — no re-fetch needed because the backend has
                    // already rotated the token, so the current URL
                    // would 404. The stub payload matches the /invites/lookup
                    // 410 detail shape so the landing renders identically.
                    setCancelledInvite({
                        code: "invite_cancelled",
                        message: "This application has been cancelled.",
                        cancelled_at: new Date().toISOString(),
                        cancelled_by_type: "customer",
                    });
                }}
            />
        </AuthLayout>
    );
}
